import React, { useContext, useEffect } from 'react';

import {
  Box,
  Dialog,
  Grid,
  IconButton,
  Paper,
  Typography,
} from '@material-ui/core';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import Close from '@material-ui/icons/Close';
import { navigate } from '@reach/router';
import { useTranslation } from '../../hooks/useTranslation';
import ROUTES from '../../routes';
import PGEButton from '../buttons/PGE-Button';
import { ContactDataInterceptModalProps } from './ContactDataIntercept.types';
import useGTMUtilContactDataIntercept from './gtm/useGTMUtilContactDataIntercept';
import {
  ContactInfoConfirmationOption,
  ContactInfoConfirmationResponse,
} from '../../__generated__/pge-types';
import useWrapWithLoader from '../../hooks/useWrapWithLoading';
import apolloClient from '../../lib/apolloClient';
import { updateContactInfoConfirmation } from './ContactDataInterceptModal.query';
import { getAccountInfoWithParams } from '../../hooks/useAccountCustomer';
import { featureFlagsContext } from '../../providers/FeatureFlagsProvider';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    contactDataModalWrapper: {
      '& .MuiDialog-paperWidthSm': {
        maxWidth: '600px',
        width: '80%',
      },
    },
    contactDataModal: {
      padding: theme.spacing(3),
      borderRadius: '5px',
      [theme.breakpoints.down('xs')]: {
        padding: theme.spacing(4, 3, 3, 3),
      },
    },
    closeBtn: {
      position: 'absolute',
      top: 24,
      right: 24,
      padding: 0,
    },
    modalBody: {
      marginTop: theme.spacing(1),
      '& > *': {
        marginTop: theme.spacing(1),
      },
    },
    contactData: {
      fontWeight: 'bold',
      wordBreak: 'break-word',
    },
    contactEmail: {
      fontWeight: 'bold',
      wordBreak: 'break-all',
    },
    updateBtn: {
      [theme.breakpoints.down('sm')]: {
        textAlign: 'center',
      },
      [theme.breakpoints.up('sm')]: {
        display: 'flex',
        justifyContent: 'flex-end',
      },
    },
    okBtn: {
      [theme.breakpoints.down('sm')]: {
        textAlign: 'center',
      },
    },
  }),
);

export default function ContactDataInterceptModal({
  open,
  onClose,
  contactData,
  setIsSessionDismissed,
}: ContactDataInterceptModalProps) {
  const { t } = useTranslation();
  const classes = useStyles();
  const {
    gtm_CDI_HandleShowModal,
    gtm_CDI_HandleDismissModal,
    gtm_CDI_HandleInformationIsCorrect,
    gtm_CDI_HandleUpdateInformation,
  } = useGTMUtilContactDataIntercept();

  const { isClosedAccountsEnabled } = useContext(featureFlagsContext);

  const { wrapWithLoader } = useWrapWithLoader();

  async function submitContactDetailsSelection(option: any) {
    const { data: submitData, errors } = await apolloClient.mutate<{
      updateContactInfoConfirmation: ContactInfoConfirmationResponse;
    }>({
      mutation: updateContactInfoConfirmation,
      variables: {
        payload: {
          contactInfoConfirmationOption: option,
        },
      },
      refetchQueries: [
        {
          query: getAccountInfoWithParams,
          variables: {
            params: {
              removeInactiveAccounts: isClosedAccountsEnabled,
            },
          },
        },
      ],
      awaitRefetchQueries: true,
      errorPolicy: 'all',
    });

    if (errors && errors.length > 0) {
      return Promise.reject('Mutation failed');
    }
    //return { isSuccess: true };
    return submitData?.updateContactInfoConfirmation;
  }

  const handleClick = wrapWithLoader(async (buttonClick: string) => {
    try {
      const result = await submitContactDetailsSelection(buttonClick);
      if (result?.success) {
        buttonClick === ContactInfoConfirmationOption.UpdateInfo
          ? await navigate(ROUTES.UPDATE_PERSONAL_INFORMATION, {
              replace: true,
            })
          : null;
        onClose();
      } else {
        await navigate(ROUTES.ERROR);
      }
    } catch (e) {
      await navigate(ROUTES.ERROR);
    }
  });
  const handleClose = () => {
    setIsSessionDismissed();
    gtm_CDI_HandleDismissModal();
    onClose();
  };

  const handleInformationIsCorrect = async () => {
    gtm_CDI_HandleInformationIsCorrect();
    await handleClick(ContactInfoConfirmationOption.InfoCorrect);
    onClose();
  };

  const handleUpdateInformation = async () => {
    gtm_CDI_HandleUpdateInformation();
    await handleClick(ContactInfoConfirmationOption.UpdateInfo);
  };

  useEffect(() => {
    gtm_CDI_HandleShowModal();
  }, []);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      scroll={'body'}
      aria-labelledby="contact-data-intercept-modal"
      aria-describedby="contact-data-intercept-modal"
      className={classes.contactDataModalWrapper}
      disableScrollLock
    >
      <Paper className={classes.contactDataModal}>
        <Box>
          <Grid container justify="space-between">
            <Typography
              variant={'body1'}
              gutterBottom
              style={{ paddingRight: '24px' }}
            >
              {t('CONTACT_DATA_REVIEW_INFO_TITLE')}
            </Typography>

            <IconButton
              aria-label="close"
              color="inherit"
              onClick={handleClose}
              className={classes.closeBtn}
            >
              <Close />
            </IconButton>
          </Grid>

          <Box className={classes.modalBody}>
            <Typography variant={'h3'}>{t('CONTACT_DATA_HEADER')}:</Typography>

            <Grid container>
              <Grid item xs={6} md={5}>
                <Typography variant="body1">{t('NAME')}:</Typography>
              </Grid>
              <Grid item xs={6} md={7}>
                <Typography variant="body1" className={classes.contactData}>
                  {contactData.name}
                </Typography>
              </Grid>
            </Grid>

            <Grid container>
              <Grid item xs={6} md={5}>
                <Typography variant="body1">{t('EMAIL')}:</Typography>
              </Grid>
              <Grid item xs={6} md={7}>
                <Typography variant="body1" className={classes.contactEmail}>
                  {contactData.email}
                </Typography>
              </Grid>
            </Grid>

            <Grid container>
              <Grid item xs={6} md={5}>
                <Typography variant="body1">{t('PHONE')}:</Typography>
              </Grid>
              <Grid item xs={6} md={7}>
                <Typography variant="body1" className={classes.contactData}>
                  {contactData.phone || t('NOT_SPECIFIED')}
                </Typography>
              </Grid>
            </Grid>

            <Grid container>
              <Grid item xs={6} md={5}>
                <Typography variant="body1">{t('MOBILE')}:</Typography>
              </Grid>
              <Grid item xs={6} md={7}>
                <Typography variant="body1" className={classes.contactData}>
                  {contactData.mobile || t('NOT_SPECIFIED')}
                </Typography>
              </Grid>
            </Grid>

            <Grid container>
              <Grid item xs={6} md={5}>
                <Typography variant="body1">
                  {t('PREFERRED_LANGUAGE')}:
                </Typography>
              </Grid>
              <Grid item xs={6} md={7}>
                <Typography variant="body1" className={classes.contactData}>
                  {contactData.prefLanguage || t('NOT_SPECIFIED')}
                </Typography>
              </Grid>
            </Grid>

            <Grid style={{ marginTop: 20 }} container spacing={2}>
              <Grid item xs={12} md={6} className={classes.okBtn}>
                <PGEButton
                  size="large"
                  color="secondary"
                  variant="outlined"
                  onClick={handleInformationIsCorrect}
                >
                  {t('INFORMATION_CORRECT')}
                </PGEButton>
              </Grid>
              <Grid className={classes.updateBtn} item xs={12} md={6}>
                <PGEButton size="large" onClick={handleUpdateInformation}>
                  {t('UPDATE_INFORMATION')}
                </PGEButton>
              </Grid>
            </Grid>
          </Box>
        </Box>
      </Paper>
    </Dialog>
  );
}
